import { useState } from "react";
import Layout from "../components/Layout.jsx";
import { getAccount } from "../lib/supabase.js";

const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";
const mono = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace";
const C = {
  ink: "#1a1a1a", dim: "#8a8a8a", faint: "#c0bdb8", line: "#e5e3de",
  bg: "#fafaf8", card: "#ffffff", accent: "#6366f1", code: "#1a1a24",
  good: "#2f9e6f", warn: "#b8862f", get: "#2f9e6f", post: "#b8862f",
};

const SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "auth", label: "Authentication" },
  { id: "endpoints", label: "Product Endpoints" },
  { id: "mcp", label: "MCP / AI Agents" },
  { id: "errors", label: "Errors & Limits" },
];

export default function ApiDocs() {
  const account = getAccount();
  const domain = account?.custom_domain || "app.morryde.com";
  const base = `https://${domain}/api/v1`;
  const [section, setSection] = useState("overview");
  const ctx = { domain, base };

  return (
    <Layout>
      <div style={{ fontFamily: sans, color: C.ink, display: "flex", gap: 34, maxWidth: 1180 }}>
        {/* local doc sub-nav */}
        <div style={{ width: 190, flexShrink: 0, position: "sticky", top: 20, alignSelf: "flex-start" }}>
          <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: C.faint, marginBottom: 12, fontWeight: 700 }}>API Reference</div>
          {SECTIONS.map(s => {
            const on = section === s.id;
            return (
              <button key={s.id} onClick={() => setSection(s.id)} style={{
                display: "block", width: "100%", textAlign: "left", background: "none", border: "none",
                borderLeft: on ? `2px solid ${C.ink}` : "2px solid transparent", padding: "7px 12px",
                fontSize: 12.5, color: on ? C.ink : C.dim, fontWeight: on ? 700 : 500, cursor: "pointer", fontFamily: sans }}>
                {s.label}
              </button>
            );
          })}
          <div style={{ margin: "16px 0 0 12px", padding: 12, background: "#eeeefc", border: `1px solid ${C.accent}33`, borderRadius: 9 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, marginBottom: 4, color: C.accent }}>Your API base</div>
            <div style={{ fontSize: 10.5, color: C.dim, fontFamily: mono, wordBreak: "break-all", lineHeight: 1.5 }}>{base}</div>
          </div>
        </div>

        {/* content */}
        <div style={{ flex: 1, minWidth: 0, maxWidth: 720 }}>
          {section === "overview" && <Overview ctx={ctx} />}
          {section === "auth" && <Auth ctx={ctx} />}
          {section === "endpoints" && <Endpoints ctx={ctx} />}
          {section === "mcp" && <Mcp ctx={ctx} />}
          {section === "errors" && <Errors />}
        </div>
      </div>
    </Layout>
  );
}

function CopyBlock({ code, lang = "bash" }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1400); };
  return (
    <div style={{ background: C.code, borderRadius: 10, margin: "14px 0", overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderBottom: "1px solid #2b2b3a" }}>
        <span style={{ fontSize: 10, color: "#7a7a8a", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: mono }}>{lang}</span>
        <button onClick={copy} style={{ background: "none", border: "1px solid #34343f", borderRadius: 6, padding: "3px 9px", color: copied ? C.good : "#9a9aac", fontSize: 11, cursor: "pointer", fontFamily: sans }}>{copied ? "Copied" : "Copy"}</button>
      </div>
      <pre style={{ margin: 0, padding: "13px 15px", fontSize: 12, lineHeight: 1.65, color: "#d4d6e4", overflow: "auto", fontFamily: mono }}>{code}</pre>
    </div>
  );
}
function H1({ children }) { return <h1 style={{ fontFamily: serif, fontStyle: "italic", fontWeight: 400, fontSize: 34, margin: "0 0 8px" }}>{children}</h1>; }
function Lead({ children }) { return <p style={{ fontSize: 14, color: C.dim, lineHeight: 1.7, margin: "0 0 22px", maxWidth: 620 }}>{children}</p>; }
function H2({ children }) { return <h2 style={{ fontSize: 15, fontWeight: 700, margin: "30px 0 6px" }}>{children}</h2>; }
function P({ children }) { return <p style={{ fontSize: 13.5, color: "#555", lineHeight: 1.7, margin: "0 0 12px", maxWidth: 620 }}>{children}</p>; }
function Cd({ children }) { return <code style={{ fontFamily: mono, fontSize: 12, background: "#f0eee9", padding: "1px 5px", borderRadius: 4, color: C.ink }}>{children}</code>; }

function Overview({ ctx }) {
  return (
    <div>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: `${C.good}18`, border: `1px solid ${C.good}44`, borderRadius: 20, padding: "3px 11px", fontSize: 11.5, color: C.good, marginBottom: 14, fontWeight: 600 }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.good }} /> Live at {ctx.domain}
      </div>
      <H1>Products API</H1>
      <Lead>Your product catalog, available as a REST API and to AI agents over MCP. Every request is scoped to your account and served from your own domain, <Cd>{ctx.domain}</Cd> — no shared endpoints, no third-party branding.</Lead>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, margin: "6px 0 8px" }}>
        {[
          ["REST", "Standard JSON over HTTPS. List, search, and fetch products with simple GET requests."],
          ["MCP", "Connect Claude, ChatGPT, or any agent. Your catalog becomes a live tool they can query."],
          ["Key auth", "Scoped API keys you issue and revoke. Read-only by default; write access is opt-in."],
          ["Your domain", "Served from your domain — every request resolves to your account automatically."],
        ].map(([t, d]) => (
          <div key={t} style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 11, padding: 15 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 5 }}>{t}</div>
            <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.55 }}>{d}</div>
          </div>
        ))}
      </div>

      <H2>How domain resolution works</H2>
      <P>Because your API lives at <Cd>{ctx.domain}</Cd>, the platform maps the incoming host to your account before the request is even authenticated. You never pass an account ID — the domain is the account. A key issued for another customer won't work against your domain, even if it were leaked.</P>

      <H2>Quick start</H2>
      <P>Fetch your first products in one request. Swap in a key from the Authentication tab.</P>
      <CopyBlock code={`curl ${ctx.base}/products \\\n  -H "Authorization: Bearer YOUR_API_KEY"`} />
    </div>
  );
}

function Auth({ ctx }) {
  const [revealed, setRevealed] = useState(false);
  const key = "clarifico_live_mor_8f3a2b9c4d1e6f7a";
  return (
    <div>
      <H1>Authentication</H1>
      <Lead>Every request needs an API key in the <Cd>Authorization</Cd> header. Keys are scoped to your account and are read-only unless you explicitly grant write access.</Lead>

      <H2>Your keys</H2>
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 11, padding: 15, margin: "10px 0 8px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Production key</div>
            <code style={{ fontSize: 12, color: C.dim, fontFamily: mono }}>{revealed ? key : "clarifico_live_mor_\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}</code>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <MiniBtn onClick={() => setRevealed(!revealed)}>{revealed ? "Hide" : "Reveal"}</MiniBtn>
            <MiniBtn>Rotate</MiniBtn>
          </div>
        </div>
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.line}`, display: "flex", gap: 8 }}>
          <Scope on>read:products</Scope>
          <Scope>write:products</Scope>
        </div>
      </div>
      <MiniBtn>+ Create new key</MiniBtn>

      <H2>Using the key</H2>
      <CopyBlock code={`curl ${ctx.base}/products \\\n  -H "Authorization: Bearer ${key}"`} />

      <H2>Scopes</H2>
      <P><Cd>read:products</Cd> grants list, get, search, and categories. <Cd>write:products</Cd> (off by default) adds create, update, and delete. Issue separate keys per integration so you can revoke one without disrupting the others.</P>
    </div>
  );
}

function Endpoints({ ctx }) {
  const eps = [
    { m: "GET", path: "/products", d: "List all products, newest first.",
      params: [["limit", "integer", "Max results (default 50, max 200)"], ["offset", "integer", "Pagination offset"], ["category", "string", "Filter by category"], ["active", "boolean", "Only active products"]] },
    { m: "GET", path: "/products/:id", d: "Fetch a single product by ID.", params: [] },
    { m: "GET", path: "/products/search", d: "Semantic + keyword search across the catalog.",
      params: [["q", "string", "Search query (required)"], ["limit", "integer", "Max results (default 10)"]] },
    { m: "GET", path: "/products/categories", d: "List all category names in use.", params: [] },
  ];
  return (
    <div>
      <H1>Product Endpoints</H1>
      <Lead>All endpoints are relative to <Cd>{ctx.base}</Cd> and return JSON. Reads are available on every plan; writes require a <Cd>write:products</Cd> key.</Lead>

      {eps.map((e, i) => (
        <div key={i} style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 11, padding: 15, marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: e.params.length ? 12 : 0 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: e.m === "GET" ? C.get : C.post, background: `${e.m === "GET" ? C.get : C.post}18`, border: `1px solid ${e.m === "GET" ? C.get : C.post}44`, borderRadius: 6, padding: "3px 8px", minWidth: 40, textAlign: "center", fontFamily: mono }}>{e.m}</span>
            <code style={{ fontSize: 13, color: C.ink, fontFamily: mono }}>{e.path}</code>
          </div>
          <div style={{ fontSize: 12.5, color: C.dim, marginBottom: e.params.length ? 12 : 0 }}>{e.d}</div>
          {e.params.length > 0 && (
            <div style={{ borderTop: `1px solid ${C.line}`, paddingTop: 10 }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: C.faint, marginBottom: 7, fontWeight: 700 }}>Query parameters</div>
              {e.params.map(([name, type, desc]) => (
                <div key={name} style={{ display: "flex", gap: 10, fontSize: 12, padding: "4px 0", alignItems: "baseline" }}>
                  <code style={{ fontFamily: mono, color: C.ink, minWidth: 70 }}>{name}</code>
                  <span style={{ color: C.accent, fontSize: 11, minWidth: 54, fontFamily: mono }}>{type}</span>
                  <span style={{ color: C.dim }}>{desc}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <H2>Example: search</H2>
      <CopyBlock code={`curl "${ctx.base}/products/search?q=suspension&limit=5" \\\n  -H "Authorization: Bearer YOUR_API_KEY"`} />
      <H2>Response</H2>
      <CopyBlock lang="json" code={`{
  "data": [
    {
      "id": "8f3a2b9c-4d1e-6f7a-b2c3-d4e5f6a7b8c9",
      "name": "CRE3000 Suspension System",
      "sku": "CRE-3000",
      "category": "suspension",
      "price": 1899.00,
      "url": "https://morryde.com/products/cre3000",
      "similarity": 0.91
    }
  ],
  "count": 1,
  "account": "morryde"
}`} />
    </div>
  );
}

function Mcp({ ctx }) {
  return (
    <div>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#eeeefc", border: `1px solid ${C.accent}44`, borderRadius: 20, padding: "3px 11px", fontSize: 11.5, color: C.accent, marginBottom: 14, fontWeight: 600 }}>
        Model Context Protocol
      </div>
      <H1>Connect to AI Agents</H1>
      <Lead>MCP lets AI assistants — Claude, ChatGPT, and others — use your product catalog as a live tool. A customer asks "which MORryde suspension fits a 2021 Class A?" and the agent queries your real data through the same account-scoped API.</Lead>

      <H2>Connection details</H2>
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 11, padding: 15, margin: "10px 0" }}>
        <Row k="Server URL" v={`https://${ctx.domain}/api/mcp`} />
        <Row k="Transport" v="HTTP (streamable)" />
        <Row k="Auth" v="Bearer API key" />
        <Row k="Tools" v="search_products, get_product, list_categories" last />
      </div>

      <H2>Add to Claude Desktop</H2>
      <P>Drop this into your <Cd>claude_desktop_config.json</Cd>:</P>
      <CopyBlock lang="json" code={`{
  "mcpServers": {
    "morryde-products": {
      "url": "https://${ctx.domain}/api/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`} />

      <H2>Available tools</H2>
      {[
        ["search_products", "Find products by natural-language query. Returns name, SKU, category, price, and a relevance score."],
        ["get_product", "Pull full detail for one product by ID — specs, description, images, and purchase links."],
        ["list_categories", "Return every category in the catalog, so the agent can narrow a search."],
      ].map(([tool, d]) => (
        <div key={tool} style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 10, padding: "12px 14px", marginBottom: 8 }}>
          <code style={{ fontSize: 12.5, color: C.ink, fontFamily: mono, fontWeight: 600 }}>{tool}</code>
          <div style={{ fontSize: 12, color: C.dim, marginTop: 4, lineHeight: 1.5 }}>{d}</div>
        </div>
      ))}
    </div>
  );
}

function Errors() {
  const codes = [
    ["200", C.good, "Success"],
    ["401", C.warn, "Missing or invalid API key"],
    ["403", C.warn, "Key lacks the required scope for this operation"],
    ["404", C.warn, "Product or resource not found"],
    ["429", C.warn, "Rate limit exceeded — slow down and retry"],
  ];
  return (
    <div>
      <H1>Errors & Limits</H1>
      <Lead>The API uses standard HTTP status codes. Error responses include a JSON body with an <Cd>error</Cd> message describing what went wrong.</Lead>

      <H2>Status codes</H2>
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 11, overflow: "hidden", margin: "10px 0" }}>
        {codes.map(([code, c, d], i) => (
          <div key={code} style={{ display: "flex", alignItems: "center", gap: 14, padding: "11px 15px", borderTop: i ? `1px solid ${C.line}` : "none" }}>
            <code style={{ fontFamily: mono, fontSize: 13, fontWeight: 700, color: c, minWidth: 34 }}>{code}</code>
            <span style={{ fontSize: 12.5, color: C.dim }}>{d}</span>
          </div>
        ))}
      </div>

      <H2>Rate limits</H2>
      <P>Read endpoints allow 600 requests per minute per key. Search is more intensive and is limited to 120 per minute. When you exceed a limit you'll receive a <Cd>429</Cd> with a <Cd>Retry-After</Cd> header telling you how many seconds to wait.</P>

      <H2>Error body</H2>
      <CopyBlock lang="json" code={`{
  "error": "Invalid API key",
  "status": 401
}`} />
    </div>
  );
}

function Row({ k, v, last }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: last ? "none" : `1px solid ${C.line}`, gap: 12 }}>
      <span style={{ fontSize: 12.5, color: C.dim }}>{k}</span>
      <code style={{ fontSize: 12, color: C.ink, fontFamily: mono, textAlign: "right" }}>{v}</code>
    </div>
  );
}
function Scope({ children, on }) {
  return <span style={{ fontSize: 11, fontWeight: 600, color: on ? C.good : C.faint, background: on ? `${C.good}15` : C.bg, border: `1px solid ${on ? C.good + "44" : C.line}`, borderRadius: 20, padding: "2px 9px", fontFamily: mono }}>{children}{on ? "" : " (off)"}</span>;
}
function MiniBtn({ children, onClick }) { return <button onClick={onClick} style={{ background: "none", border: `1px solid ${C.line}`, color: C.dim, borderRadius: 7, padding: "6px 12px", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: sans }}>{children}</button>; }
